<template>
    <dx-data-grid
        ref="notebookWidgetRef"
        :key-expr="meta.keyExpr"
        :data-source="dataSource"
        class="flex flex-col h-full gap-2"
        :allow-column-resizing="true"
        :allow-column-reordering="true"
        :focused-row-enabled="meta.focusedRowEnabled"
        :focused-row-index="0"
        :hover-state-enabled="true"
        :column-min-width="70"
        :column-auto-width="meta.columnAutoWidth || false"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="true"
        :filter-row="{ visible: true }"
        :header-filter="{ visible: true }"
        :filter-panel="{ visible: true }"
        :scrolling="{ mode: 'virtual' }"
        :selection="{ mode: 'multiple', showCheckBoxesMode: 'always' }"
        :search-panel="{ visible: true, placeholder: 'Поиск...' }"
        :column-chooser="{ enabled: true, mode: 'select' }"
        :group-panel="{ visible: true }"
        :fixing="{ enabled: true }"
        :grouping="{ contextMenuEnabled: true }"
        :load-panel="{ enabled: true }"
        @optionChanged="optionChanged"
        @selectionChanged="selectionChanged"
    >
        <dx-editing mode="batch" :allow-adding="true" :allow-updating="true" :allow-deleting="true" :use-icons="true" />
        <dx-column
            v-for="(item, index) in meta.columns"
            :key="index"
            :caption="item.caption"
            :data-field="item.dataField"
            :visible="item.visible"
            :cell-template="item.cellTemplate"
            :edit-cell-template="item.editCellTemplate"
            :editor-options="item.editorOptions"
            :lookup="item.lookup || null"
            :group-index="item.groupIndex"
            :data-type="item.dataType || 'string'"
            :calculate-cell-value="item.calculateCellValue"
            :calculate-display-value="item.calculateDisplayValue"
            :width="item.width"
            :sort-index="item.sortIndex"
            :sort-order="item.sortOrder"
            :validation-rules="item.validationRules"
            :allow-editing="item.allowEditing"
        />

        <template #textBoxEditor="{ data }">
            <dx-text-area
                class="min-h-[250px]"
                :value="data.data.text"
                @value-changed="event => onTextAreaValueChanged(event, data)"
            />
        </template>

        <dx-toolbar>
            <dx-item location="after">
                <dx-button icon="refresh" hint="Обновить" @click="refresh" />
            </dx-item>

            <dx-item location="after">
                <dx-button icon="copy" hint="Копировать" :disabled="selected" @click="copyRow" />
            </dx-item>

            <dx-item location="after">
                <dx-button icon="add" hint="Добавить" @click="addPredefinedRow" />
            </dx-item>

            <dx-item location="after">
                <dx-button icon="trash" hint="Удалить" :disabled="selected" @click="deleteRows" />
            </dx-item>

            <dx-item location="after">
                <dx-button icon="floppy" hint="Сохранить" :disabled="changed" @click="saveChanges" />
            </dx-item>

            <dx-item
                v-if="typeof meta.title === 'string' && !!meta.title.length"
                locate-in-menu="auto"
                location="before"
                :text="meta.title"
            />

            <dx-item location="after" name="searchPanel" locate-in-menu="auto" />
        </dx-toolbar>
    </dx-data-grid>
</template>

<script setup>
import { ref } from 'vue';
import DataSource from 'devextreme/data/data_source';
import { DxDataGrid, DxColumn, DxEditing, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import { DxTextArea, DxButton } from 'devextreme-vue';

const line = { text: 'some text', date: new Date(0), area: null };
const list = [];
for (let i = 0; i < 5; i++) {
    line.id = i;
    line.text += i;
    line.area = 0;
    list.push({ ...line });
}

const dataSource = ref(null);
dataSource.value = new DataSource({ reshapeOnPush: true, load: () => list });

const notebookWidgetRef = ref(null);

const areaList = [
    { id: 0, label: 'Не выбрана' },
    { id: 1, label: 'Обходы потребителей' },
    { id: 2, label: 'ПУ и метрология' },
    { id: 3, label: 'Юристы' },
    { id: 4, label: 'Карт. метрология' },
];

const meta = {
    columns: [
        {
            dataField: 'id',
            caption: 'ID',
            visible: false,
        },
        {
            dataField: 'text',
            caption: 'Текст записной книжки',
            editCellTemplate: 'textBoxEditor',
        },
        {
            dataField: 'date',
            caption: 'Дата ввода',
            dataType: 'date',
        },
        {
            dataField: 'area',
            caption: 'Область применения',
            lookup: {
                dataSource: areaList,
                valueExpr: 'id',
                displayExpr: 'label',
            },
        },
    ],
};

const onTextAreaValueChanged = (event, cell) => {
    cell.setValue(event.value);
    cell.component.updateDimensions();
};

const refresh = () => {
    console.log('Данные успешно обновлены');
};

const selected = ref(true);
const selectionChanged = event => {
    selected.value = !event.selectedRowKeys.length;
};

const addRow = async (data, newOption) => {
    dataSource.value.store().push([{ data, type: 'insert' }]);

    const changesOption = notebookWidgetRef.value.instance.option('editing.changes');
    changesOption.push(newOption);
    notebookWidgetRef.value.instance.option('editing.changes', changesOption);
};

const addPredefinedRow = () => {
    const data = { date: Date.now(), area: 0 };
    const newOption = {
        data,
        type: 'insert',
        key: Math.random(),
    };

    addRow(data, newOption);
};

const copyRow = async () => {
    const selectedRowData = notebookWidgetRef.value.instance.getSelectedRowsData();
    const selectedRowKeys = notebookWidgetRef.value.instance.getSelectedRowKeys();

    const data = {
        text: await selectedRowData[0].text,
        date: Date.now(),
        area: 0,
    };
    const newOption = {
        data,
        type: 'insert',
        key: Math.random(),
        insertAfterKey: selectedRowKeys[0],
    };

    await addRow(data, newOption);
};

const deleteRows = () => {
    const selectedRowKeys = notebookWidgetRef.value.instance.getSelectedRowKeys();

    selectedRowKeys.forEach(key => {
        const index = notebookWidgetRef.value.instance.getRowIndexByKey(key);
        notebookWidgetRef.value.instance.deleteRow(index);
    });
    notebookWidgetRef.value.instance.deselectAll();
};

const changed = ref(true);
const optionChanged = event => {
    if (event.fullName === 'editing.changes') {
        changed.value = !event.value.length;
    }
};

const saveChanges = () => {
    notebookWidgetRef.value.instance.option('editing.changes');
};
</script>
